export type ApprovalPlan = 'free' | 'pro' | 'premium';
export type ApprovalStatus = 'pending_approval' | 'approved' | 'rejected' | 'needs_changes' | 'suspended';

export interface RadioApproval {
  id: number;
  radioName: string;
  frequency: string;
  slug: string;
  streamUrl: string;
  ownerName: string;
  ownerEmail: string;
  city: string;
  country: string;
  category: string;
  plan: ApprovalPlan;
  status: ApprovalStatus;
  submittedAt: string;
  logoUrl: string;
  brandingColors: string[];
  brandingDescription: string;
  description: string;
  rejectionReason?: string;
  changeRequestComment?: string;
  suspensionReason?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  frequencyReserved: boolean;
  frequencyReservedAt?: string;
  frequencyExpiresAt?: string;
  contractNumber: string;
  initialPayment: number;
  initialPaymentCurrency: string;
}

export const planLabels: Record<ApprovalPlan, string> = {
  free: 'Free',
  pro: 'Pro',
  premium: 'Premium',
};

export const statusLabels: Record<ApprovalStatus, string> = {
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  needs_changes: 'Needs Changes',
  suspended: 'Suspended',
};

export const radioApprovalsData: RadioApproval[] = [
  {
    id: 1,
    radioName: 'Radio Nueva Actitud',
    frequency: '94.70 RFW',
    slug: 'radio-nueva-actitud',
    streamUrl: 'https://stream.nuevaactitud.com/live',
    ownerName: 'Carlos Mendoza',
    ownerEmail: 'carlos@nuevaactitud.com',
    city: 'Santa Rosa',
    country: 'México',
    category: 'Pop',
    plan: 'pro',
    status: 'pending_approval',
    submittedAt: '2026-07-12T14:30:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20radio%20station%20logo%20design%20with%20musical%20notes%20and%20frequency%20waves%2C%20clean%20circular%20emblem%20style%2C%20warm%20golden%20and%20white%20color%20palette%2C%20simple%20geometric%20shapes%2C%20professional%20broadcast%20aesthetic%2C%20isolated%20on%20transparent%20background%2C%20high%20contrast%20vector%20style&width=200&height=200&seq=app-logo-104&orientation=squarish',
    brandingColors: ['#D4A574', '#1A1A1A', '#FAFAFA'],
    brandingDescription: 'Estilo moderno minimalista. Paleta cálida con dorados y negros. Tipografía sans-serif bold para titulares, interlineado generoso. Imágenes editoriales con tonos cálidos.',
    description: 'Radio pop contemporánea con programación en vivo 24/7. Música, entrevistas y entretenimiento.',
    frequencyReserved: false,
    contractNumber: 'CT-2026-0842',
    initialPayment: 0,
    initialPaymentCurrency: 'USD',
  },
  {
    id: 2,
    radioName: 'Rock Total Radio',
    frequency: '87.01 RFW',
    slug: 'rock-total-radio',
    streamUrl: 'https://cdn.rocktotal.fm/stream',
    ownerName: 'Valentina Ríos',
    ownerEmail: 'valentina@rocktotal.fm',
    city: 'Buenos Aires',
    country: 'Argentina',
    category: 'Rock',
    plan: 'premium',
    status: 'approved',
    submittedAt: '2026-06-28T09:15:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20electric%20guitar%20and%20lightning%20bolt%2C%20circular%20design%20black%20and%20red%20color%20scheme%2C%20modern%20rock%20broadcast%20emblem%2C%20energetic%20and%20bold%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=app-logo-102&orientation=squarish',
    brandingColors: ['#CC3333', '#111111', '#F5F0EB'],
    brandingDescription: 'Estética rockera con rojos intensos y negros. Tipografía bold condensada. Fotografía en blanco y negro con acentos rojos.',
    description: 'Rock clásico y contemporáneo. Las mejores guitarras del mundo en un solo lugar.',
    reviewedAt: '2026-07-01T11:00:00Z',
    reviewedBy: 'Admin Global',
    frequencyReserved: true,
    frequencyReservedAt: '2026-07-01T11:00:00Z',
    frequencyExpiresAt: '2026-08-01T11:00:00Z',
    contractNumber: 'CT-2026-0791',
    initialPayment: 299,
    initialPaymentCurrency: 'USD',
  },
  {
    id: 3,
    radioName: 'Radio Comunitaria Unida',
    frequency: '87.02 RFW',
    slug: 'radio-comunitaria-unida',
    streamUrl: 'https://live.comunitariaunida.org/stream',
    ownerName: 'Miguel Ángel Torres',
    ownerEmail: 'miguel@comunitariaunida.org',
    city: 'Santiago',
    country: 'Chile',
    category: 'Comunitaria',
    plan: 'free',
    status: 'pending_approval',
    submittedAt: '2026-07-14T16:45:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20station%20logo%20with%20people%20holding%20hands%20in%20circle%2C%20minimalist%20design%20with%20warm%20earth%20tones%2C%20green%20and%20orange%20colors%2C%20inclusive%20broadcast%20emblem%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=app-logo-103&orientation=squarish',
    brandingColors: ['#4A7C59', '#E8A87C', '#FFF8F0'],
    brandingDescription: 'Diseño inclusivo y cálido. Colores tierra y verdes. Tipografía redondeada amigable. Fotografía comunitaria y local.',
    description: 'La voz de nuestra comunidad. Cultura, eventos locales y participación ciudadana.',
    frequencyReserved: false,
    contractNumber: 'CT-2026-0851',
    initialPayment: 0,
    initialPaymentCurrency: 'USD',
  },
  {
    id: 4,
    radioName: 'Noticias 24/7 Radio',
    frequency: '107.90 RFW',
    slug: 'noticias-24-7-radio',
    streamUrl: 'https://edge.noticias247.com/live',
    ownerName: 'Patricia Luna',
    ownerEmail: 'patricia@noticias247.com',
    city: 'Ciudad de México',
    country: 'México',
    category: 'Noticias',
    plan: 'premium',
    status: 'rejected',
    submittedAt: '2026-07-05T08:00:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=News%20radio%20station%20logo%20design%20with%20microphone%20and%20globe%20icon%2C%20circular%20minimalist%20emblem%2C%20slate%20and%20white%20professional%20color%20scheme%2C%20broadcast%20journalism%20style%2C%20isolated%20on%20transparent%20background%2C%20clean%20vector%20design&width=200&height=200&seq=app-logo-107&orientation=squarish',
    brandingColors: ['#2C3E50', '#ECF0F1', '#E74C3C'],
    brandingDescription: 'Estilo periodístico serio. Paleta azul marino y grises. Tipografía serif para titulares. Fotoperiodismo de alta calidad.',
    description: 'Información actualizada las 24 horas. Noticias locales e internacionales.',
    rejectionReason: 'El stream URL no responde correctamente. Se detectaron cortes frecuentes durante la prueba de calidad. Favor corregir el encoding del servidor.',
    reviewedAt: '2026-07-08T14:30:00Z',
    reviewedBy: 'Admin Global',
    frequencyReserved: false,
    contractNumber: 'CT-2026-0823',
    initialPayment: 299,
    initialPaymentCurrency: 'USD',
  },
  {
    id: 5,
    radioName: 'La Kalle 99.9',
    frequency: '99.90 RFW',
    slug: 'la-kalle-99-9',
    streamUrl: 'https://stream.lakalle999.com/live',
    ownerName: 'Diego Fernández',
    ownerEmail: 'diego@lakalle999.com',
    city: 'Bogotá',
    country: 'Colombia',
    category: 'Pop',
    plan: 'pro',
    status: 'needs_changes',
    submittedAt: '2026-07-10T11:20:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=Pop%20music%20radio%20station%20logo%20with%20headphones%20and%20music%20equalizer%20bars%2C%20circular%20design%20vibrant%20pink%20and%20coral%20colors%2C%20modern%20youthful%20broadcast%20emblem%2C%20energetic%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=app-logo-105&orientation=squarish',
    brandingColors: ['#FF6B6B', '#FFE66D', '#1A1A2E'],
    brandingDescription: 'Estética vibrante y juvenil. Colores neón y oscuros. Tipografía bold moderna. Visuales dinámicos y enérgicos.',
    description: 'Los éxitos del momento y la música que está marcando tendencia.',
    changeRequestComment: 'El logo tiene baja resolución. Necesitamos una versión en 512x512px mínimo. También ajustar la descripción para incluir los géneros musicales específicos.',
    reviewedAt: '2026-07-13T10:00:00Z',
    reviewedBy: 'Admin Global',
    frequencyReserved: false,
    contractNumber: 'CT-2026-0805',
    initialPayment: 149,
    initialPaymentCurrency: 'USD',
  },
  {
    id: 6,
    radioName: 'Deportes Total Radio',
    frequency: '120.00 RFW',
    slug: 'deportes-total-radio',
    streamUrl: 'https://cdn.deportestotal.fm/stream',
    ownerName: 'Andrés Martínez',
    ownerEmail: 'andres@deportestotal.fm',
    city: 'Madrid',
    country: 'España',
    category: 'Deportes',
    plan: 'premium',
    status: 'approved',
    submittedAt: '2026-06-15T07:30:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20trophy%20icon%2C%20circular%20design%20dark%20navy%20and%20gold%20color%20scheme%2C%20dynamic%20broadcast%20emblem%2C%20energetic%20sports%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=app-logo-108&orientation=squarish',
    brandingColors: ['#1B2A4A', '#F5A623', '#FFFFFF'],
    brandingDescription: 'Identidad deportiva premium. Azul marino y dorado. Tipografía bold itálica. Fotografía deportiva de alto impacto.',
    description: 'La cobertura deportiva más completa. Fútbol, béisbol, baloncesto y más.',
    reviewedAt: '2026-06-20T09:00:00Z',
    reviewedBy: 'Admin Global',
    frequencyReserved: true,
    frequencyReservedAt: '2026-06-20T09:00:00Z',
    frequencyExpiresAt: '2026-07-20T09:00:00Z',
    contractNumber: 'CT-2026-0690',
    initialPayment: 299,
    initialPaymentCurrency: 'USD',
  },
  {
    id: 7,
    radioName: 'Radio Vida Nueva',
    frequency: '180.00 RFW',
    slug: 'radio-vida-nueva',
    streamUrl: 'https://stream.vidanueva.fm/live',
    ownerName: 'Sofía Ramírez',
    ownerEmail: 'sofia@vidanueva.fm',
    city: 'Lima',
    country: 'Perú',
    category: 'Cristiana',
    plan: 'free',
    status: 'pending_approval',
    submittedAt: '2026-07-15T10:00:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=Christian%20worship%20radio%20station%20logo%20with%20cross%20and%20sunrise%20symbols%2C%20circular%20design%20soft%20ivory%20and%20white%20colors%2C%20peaceful%20spiritual%20broadcast%20emblem%2C%20minimalist%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=app-logo-111&orientation=squarish',
    brandingColors: ['#F5E6D3', '#8B7355', '#FFF8F0'],
    brandingDescription: 'Estilo espiritual sereno. Tonos crema y marfil. Tipografía serif elegante. Imágenes de naturaleza y amaneceres.',
    description: 'Música de adoración y mensajes inspiradores para toda la familia cristiana.',
    frequencyReserved: false,
    contractNumber: 'CT-2026-0860',
    initialPayment: 0,
    initialPaymentCurrency: 'USD',
  },
  {
    id: 8,
    radioName: 'Electro Vibes FM',
    frequency: '96.50 RFW',
    slug: 'electro-vibes-fm',
    streamUrl: 'https://edge.electrovibes.fm/stream',
    ownerName: 'Lucas Oliveira',
    ownerEmail: 'lucas@electrovibes.fm',
    city: 'São Paulo',
    country: 'Brasil',
    category: 'Electrónica',
    plan: 'pro',
    status: 'suspended',
    submittedAt: '2026-05-20T14:00:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=Electronic%20music%20radio%20station%20logo%20with%20synthesizer%20waveform%20and%20neon%20accents%2C%20circular%20design%20vibrant%20cyan%20and%20magenta%20colors%2C%20futuristic%20broadcast%20emblem%2C%20energetic%20club%20music%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=app-logo-114&orientation=squarish',
    brandingColors: ['#00F5D4', '#F15BB5', '#0A0A0A'],
    brandingDescription: 'Estética futurista y club. Neón cyan y magenta sobre negro. Tipografía tech monoespaciada. Visuales generativos y abstractos.',
    description: 'House, techno y electrónica 24/7. Los mejores DJs mezclando en vivo.',
    suspensionReason: 'Detectado uso de contenido con copyright sin licencia. Se requiere verificación de derechos de reproducción.',
    reviewedAt: '2026-07-01T08:00:00Z',
    reviewedBy: 'Admin Global',
    frequencyReserved: false,
    contractNumber: 'CT-2026-0712',
    initialPayment: 149,
    initialPaymentCurrency: 'USD',
  },
  {
    id: 9,
    radioName: 'Radio Salsa Brava',
    frequency: '110.70 RFW',
    slug: 'radio-salsa-brava',
    streamUrl: 'https://stream.salsabrava.co/live',
    ownerName: 'Carmen Rodríguez',
    ownerEmail: 'carmen@salsabrava.co',
    city: 'Cali',
    country: 'Colombia',
    category: 'Salsa',
    plan: 'pro',
    status: 'pending_approval',
    submittedAt: '2026-07-13T09:00:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=Salsa%20music%20radio%20station%20logo%20with%20congas%20and%20tropical%20palm%20silhouette%2C%20circular%20design%20vibrant%20orange%20and%20yellow%20colors%2C%20festive%20Caribbean%20broadcast%20emblem%2C%20energetic%20Latin%20rhythm%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=app-logo-115&orientation=squarish',
    brandingColors: ['#FF6F00', '#FFB300', '#1C1C1C'],
    brandingDescription: 'Identidad tropical y festiva. Naranjas y amarillos vibrantes. Tipografía bold con curvas. Fotografía de baile y música en vivo.',
    description: 'La mejor salsa, timba y son cubano. Ritmo latino las 24 horas del día.',
    frequencyReserved: false,
    contractNumber: 'CT-2026-0835',
    initialPayment: 149,
    initialPaymentCurrency: 'USD',
  },
  {
    id: 10,
    radioName: 'Radio Hip Hop Nation',
    frequency: '170.40 RFW',
    slug: 'radio-hip-hop-nation',
    streamUrl: 'https://cdn.hiphopnation.fm/stream',
    ownerName: 'Marcus Johnson',
    ownerEmail: 'marcus@hiphopnation.fm',
    city: 'Atlanta',
    country: 'Estados Unidos',
    category: 'Hip Hop',
    plan: 'premium',
    status: 'pending_approval',
    submittedAt: '2026-07-11T15:00:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=Hip%20hop%20radio%20station%20logo%20with%20turntable%20and%20graffiti%20style%20crown%2C%20circular%20design%20bold%20black%20and%20gold%20colors%2C%20urban%20broadcast%20emblem%2C%20street%20music%20culture%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=app-logo-118&orientation=squarish',
    brandingColors: ['#FFD700', '#1A1A1A', '#F5F5F5'],
    brandingDescription: 'Estética urbana y bold. Negro y dorado. Tipografía graffiti-moderna. Fotografía street style y retratos de artistas.',
    description: 'Hip hop, rap y cultura urbana. Los beats más duros desde Atlanta.',
    frequencyReserved: false,
    contractNumber: 'CT-2026-0818',
    initialPayment: 299,
    initialPaymentCurrency: 'USD',
  },
  {
    id: 11,
    radioName: 'Radio Country Road',
    frequency: '190.20 RFW',
    slug: 'radio-country-road',
    streamUrl: 'https://stream.countryroad.fm/live',
    ownerName: 'Billy Ray Thompson',
    ownerEmail: 'billy@countryroad.fm',
    city: 'Nashville',
    country: 'Estados Unidos',
    category: 'Country',
    plan: 'free',
    status: 'needs_changes',
    submittedAt: '2026-07-08T06:30:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=Country%20music%20radio%20station%20logo%20with%20acoustic%20guitar%20and%20cowboy%20hat%2C%20circular%20design%20warm%20brown%20and%20cream%20colors%2C%20rustic%20broadcast%20emblem%2C%20authentic%20Nashville%20country%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=app-logo-119&orientation=squarish',
    brandingColors: ['#8B4513', '#DEB887', '#FFF8DC'],
    brandingDescription: 'Estilo rústico y auténtico. Marrones y cremas. Tipografía slab serif. Fotografía rural y de conciertos acústicos.',
    description: 'Country clásico y moderno. Desde el corazón de Nashville, Tennessee.',
    changeRequestComment: 'El stream URL apunta a un servidor con latencia alta (+300ms). Recomendamos migrar a un CDN compatible. También falta la descripción de la programación.',
    reviewedAt: '2026-07-12T16:00:00Z',
    reviewedBy: 'Admin Global',
    frequencyReserved: false,
    contractNumber: 'CT-2026-0845',
    initialPayment: 0,
    initialPaymentCurrency: 'USD',
  },
  {
    id: 12,
    radioName: 'Radio Reggae Vibes',
    frequency: '150.30 RFW',
    slug: 'radio-reggae-vibes',
    streamUrl: 'https://edge.reggaevibes.jm/stream',
    ownerName: 'Denzel Marley',
    ownerEmail: 'denzel@reggaevibes.jm',
    city: 'Kingston',
    country: 'Jamaica',
    category: 'Reggae',
    plan: 'pro',
    status: 'rejected',
    submittedAt: '2026-06-20T12:00:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=Reggae%20radio%20station%20logo%20with%20lion%20of%20Judah%20and%20palm%20tree%2C%20circular%20design%20vibrant%20green%20yellow%20and%20red%20colors%2C%20authentic%20Jamaican%20broadcast%20emblem%2C%20chilled%20island%20music%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=app-logo-117&orientation=squarish',
    brandingColors: ['#009B3A', '#FED100', '#000000'],
    brandingDescription: 'Identidad caribeña y relajada. Verde, amarillo y negro rastafari. Tipografía bold redondeada. Fotografía de playa y cultura jamaiquina.',
    description: 'Reggae, dancehall y ritmos caribeños. Buenas vibras desde Jamaica.',
    rejectionReason: 'La señal de audio tiene problemas de calidad. Se requiere un bitrate mínimo de 128kbps en stereo. El stream actual entrega 64kbps mono.',
    reviewedAt: '2026-06-25T13:00:00Z',
    reviewedBy: 'Admin Global',
    frequencyReserved: false,
    contractNumber: 'CT-2026-0755',
    initialPayment: 149,
    initialPaymentCurrency: 'USD',
  },
  {
    id: 13,
    radioName: 'Radio Infantil Aventura',
    frequency: '97.80 RFW',
    slug: 'radio-infantil-aventura',
    streamUrl: 'https://stream.infantilaventura.com/live',
    ownerName: 'Laura Benítez',
    ownerEmail: 'laura@infantilaventura.com',
    city: 'Montevideo',
    country: 'Uruguay',
    category: 'Infantil',
    plan: 'free',
    status: 'pending_approval',
    submittedAt: '2026-07-14T08:15:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=Childrens%20radio%20station%20logo%20with%20cartoon%20rocket%20and%20stars%2C%20circular%20design%20playful%20bright%20orange%20and%20yellow%20colors%2C%20fun%20educational%20broadcast%20emblem%2C%20kid%20friendly%20cheerful%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=app-logo-120&orientation=squarish',
    brandingColors: ['#FF9500', '#FFD60A', '#FFFFFF'],
    brandingDescription: 'Estilo infantil y alegre. Naranjas y amarillos brillantes. Tipografía bubble redondeada. Ilustraciones cartoon y personajes animados.',
    description: 'Contenido educativo y divertido para los más pequeños. Cuentos, música y aventuras.',
    frequencyReserved: false,
    contractNumber: 'CT-2026-0857',
    initialPayment: 0,
    initialPaymentCurrency: 'USD',
  },
  {
    id: 14,
    radioName: 'Clásica Mundial Radio',
    frequency: '130.50 RFW',
    slug: 'clasica-mundial-radio',
    streamUrl: 'https://cdn.clasicamundial.fm/stream',
    ownerName: 'Antonio Vivaldi Jr.',
    ownerEmail: 'antonio@clasicamundial.fm',
    city: 'Viena',
    country: 'Austria',
    category: 'Clásica',
    plan: 'premium',
    status: 'suspended',
    submittedAt: '2026-04-10T10:00:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=Classical%20music%20radio%20station%20logo%20with%20violin%20and%20conductor%20baton%2C%20circular%20design%20elegant%20cream%20and%20burgundy%20colors%2C%20refined%20broadcast%20emblem%2C%20sophisticated%20orchestral%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=app-logo-116&orientation=squarish',
    brandingColors: ['#800020', '#F5E6D3', '#2C2C2C'],
    brandingDescription: 'Estética clásica y refinada. Borgoña y crema. Tipografía serif clásica. Fotografía de orquestas y partituras.',
    description: 'Las obras maestras de la música clásica. Beethoven, Mozart, Bach y más.',
    suspensionReason: 'Plan Premium con pago rechazado por tercer mes consecutivo. Se requiere actualizar método de pago.',
    reviewedAt: '2026-07-01T09:00:00Z',
    reviewedBy: 'Admin Global',
    frequencyReserved: false,
    contractNumber: 'CT-2026-0640',
    initialPayment: 299,
    initialPaymentCurrency: 'USD',
  },
  {
    id: 15,
    radioName: 'Radio Jazz Nocturno',
    frequency: '91.30 RFW',
    slug: 'jazz-nocturno-radio',
    streamUrl: 'https://stream.jazznocturno.fm/live',
    ownerName: 'Ella Fitzgerald III',
    ownerEmail: 'ella@jazznocturno.fm',
    city: 'Nueva Orleans',
    country: 'Estados Unidos',
    category: 'Jazz',
    plan: 'pro',
    status: 'approved',
    submittedAt: '2026-05-15T16:00:00Z',
    logoUrl: 'https://readdy.ai/api/search-image?query=Jazz%20radio%20station%20logo%20with%20saxophone%20and%20crescent%20moon%2C%20circular%20design%20deep%20amber%20and%20warm%20gold%20colors%2C%20smooth%20sophisticated%20broadcast%20emblem%2C%20classic%20New%20Orleans%20jazz%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=app-logo-113&orientation=squarish',
    brandingColors: ['#C77D20', '#1C110A', '#FDF8F3'],
    brandingDescription: 'Estilo nocturno y sofisticado. Ámbar profundo y negro cálido. Tipografía serif elegante. Fotografía de clubs de jazz y músicos.',
    description: 'El mejor jazz clásico y contemporáneo. Desde el corazón de Nueva Orleans para el mundo.',
    reviewedAt: '2026-05-20T11:00:00Z',
    reviewedBy: 'Admin Global',
    frequencyReserved: true,
    frequencyReservedAt: '2026-05-20T11:00:00Z',
    frequencyExpiresAt: '2026-06-20T11:00:00Z',
    contractNumber: 'CT-2026-0588',
    initialPayment: 149,
    initialPaymentCurrency: 'USD',
  },
];

export const planFilterKeys = ['todas', 'free', 'pro', 'premium', 'pendientes'] as const;
export type PlanFilterKey = typeof planFilterKeys[number];